import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { T, useTranslate } from '@tolgee/react'
import classNames from 'classnames'
import { ContextField } from 'components/document-context/ContextField'
import { FieldDescription } from 'components/field-description'
import { useLanguages } from 'components/hooks/language'
import usePanelSize from 'components/hooks/panelSize'
import { transformReadyMadeTemplatesData, transformSelectOptionValue } from 'helpers'
import aiController from 'helpers/AIController'
import MoreOptions from 'toolComponents/generic/more-options'
import { ButtonGroup, Input, ReactSelect } from 'ui'
import WriterLengthContainer from '../WriterLengthContainer'
import { Creativity, replyTextActions } from '../writer-options-data'
import { replyTextReadyMadeTemplates } from '../writer-options-data/ready-made-templates'

export default function ReplyTextOptions({
  visible,
  dispatch,
  actionTypes,
  setWordLength,
  setTextToReply,
  setWhatToReply,
  wordLength,
  setWriterLengthOption,
  writerLengthOption,
  setCreativity,
  creativity,
  optionsData,
  templateOptions,
  setTemplateId,
  allowStandard,
  showUpgradePlanModal,
  templateId,
  handleDeleteTemplate,
  isShowAdvanced,
  setIsShowAdvanced,
  isGuruModeEnabled,
  setIsGuruModeEnabled,
  documentContext,
  model,
  setModel,
}) {
  const isPanelLarge = usePanelSize('reply')
  const { t } = useTranslate()

  const { defaultToolOutPutLanguage, languages } = useLanguages()

  const readyMadeTemplatesData = transformReadyMadeTemplatesData(replyTextReadyMadeTemplates, t)

  const handleTopicChange = ({ value, name }) => {
    dispatch({ type: actionTypes[`SET_${name}`], payload: value })
  }

  const handleRadioButtonCheck = ({ radioBoxName, checked }) => {
    setWordLength({ id: radioBoxName, checked })
  }

  const handleSelectTemplate = (e) => {
    if (!allowStandard) return showUpgradePlanModal()

    setTemplateId(e)
  }

  return (
    visible && (
      <div
        className={classNames(
          'break-words',
          isPanelLarge
            ? 'grid-cols-1 gap-[14px] space-y-4 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0'
            : 'space-y-4'
        )}
      >
        <div className='col-span-2'>
          <ContextField documentContext={documentContext} />
        </div>

        {/* Template */}
        <div className='space-y-[6px]' data-tooltip-id='writer-reply-template'>
          <FieldDescription>
            <T keyName='eleo-write-text-template'>Template</T>
          </FieldDescription>
          <ReactSelect
            options={templateOptions}
            isClearable
            clearLabel={t('eleo-clear-button-label-none', 'None')}
            placeHolder={t('eleo-select-drop-down-placeholder', 'Select')}
            customOptions={readyMadeTemplatesData}
            // defaultValue={options[0]}
            setSelectedValue={handleSelectTemplate}
            value={templateId.value ? templateId : null}
            showDeleteIcon
            deleteAction={(templateId) => handleDeleteTemplate(templateId)}
            name='TEMPLATE'
          />
        </div>

        {/* Model */}
        <div className='space-y-[6px]'>
          <FieldDescription>
            <T keyName='eleo-chat-settings-model'>Model</T>
          </FieldDescription>

          <div className='h-[36px]'>
            <ReactSelect
              options={aiController.getSupportedTextModels({
                includeReasoning: false,
                includeOnline: false,
              })}
              setSelectedValue={({ value }) => {
                setModel({
                  ...aiController.getSupportedTextModels().find((model) => model.value === value),
                  description: undefined,
                })
              }}
              value={model}
              menuPlacement='bottom'
              menuAlignment='right'
            />
          </div>
        </div>

        {/* Text to reply to */}
        <div
          id='text-to-reply-to'
          className='col-span-2 space-y-[6px]'
          data-tooltip-id='writer-reply-myText'
        >
          <FieldDescription>
            <T keyName='eleo-tool-reply-text-to-reply-to'>Text To Reply To</T>
          </FieldDescription>
          <Input
            placeholder={t(
              'eleo-reply-text-to-reply-placeholder',
              'Provide more details about your text'
            )}
            className='placeholder-font-regular min-h-[220px] w-full rounded-[8px] text-[13px] placeholder-[#36363680]'
            onChange={setTextToReply}
            value={optionsData.textToReply}
            isTextArea
          />
        </div>

        {/* What to reply */}
        <div
          id='what-to-reply'
          className='col-span-2 space-y-[6px]'
          data-tooltip-id='writer-reply-whatToDo'
        >
          <FieldDescription>
            <T keyName='eleo-tool-reply-what-to-reply'>What To Reply?</T>
          </FieldDescription>
          <Input
            placeholder={t('eleo-reply-what-to-reply-placeholder', 'What To Reply?')}
            className='placeholder-font-regular w-full rounded-[8px] text-[13px]  placeholder-[#36363680]'
            value={optionsData.whatToReply}
            onChange={setWhatToReply}
          />
        </div>

        {/* Show advanced button */}
        <div className='writer-tour-options col-span-2 flex items-center justify-center text-[14px]'>
          <button
            className={classNames(
              'text-brand-text-primary flex w-auto items-center justify-center rounded-[4px]',
              isShowAdvanced ? 'bg-brand-secondary' : 'bg-brand-green'
            )}
            onClick={() => setIsShowAdvanced((prev) => !prev)}
          >
            <div className='ml-[5px]'>
              <MoreOptions isShowAdvanced={isShowAdvanced} />
            </div>
            {isShowAdvanced ? (
              <ChevronLeft className='rotate-90' />
            ) : (
              <ChevronRight className='rotate-90' />
            )}
          </button>
        </div>

        {/* Advanced section */}
        <div
          className={classNames(
            'col-span-2 flex flex-col',
            !isShowAdvanced && 'hidden',
            isPanelLarge ? 'grid grid-cols-1 gap-[14px] lg:grid-cols-2 lg:gap-4' : 'space-y-4'
          )}
        >
          {/* Write as */}
          <div className='space-y-[6px]' data-tooltip-id='writer-reply-actions'>
            <FieldDescription>
              <T keyName='eleo-tool-reply-write-a-reply-as'>Write A Reply As:</T>
            </FieldDescription>
            <div className='!w-full'>
              <ReactSelect
                options={replyTextActions}
                placeHolder={t('eleo-select-drop-down-placeholder', 'Select')}
                // defaultValue={options[0]}
                setSelectedValue={handleTopicChange}
                value={transformSelectOptionValue(replyTextActions, optionsData.state.action, null)}
                name='ACTION'
                menuPlacement='top'
                isClearable
                clearLabel={t('eleo-clear-button-label-none', 'None')}
              />
            </div>
          </div>

          {/* Language */}
          <div className='space-y-[6px]' data-tooltip-id='writer-reply-language'>
            <FieldDescription>
              <T keyName='eleo-write-text-language'>Language</T>
            </FieldDescription>
            <div className='!w-full'>
              <ReactSelect
                options={languages}
                setSelectedValue={handleTopicChange}
                value={transformSelectOptionValue(
                  languages,
                  optionsData.state.language,
                  defaultToolOutPutLanguage
                )}
                name='LANGUAGE'
                menuPlacement='top'
              />
            </div>
          </div>

          {/* Length */}
          <div className='col-span-2 space-y-[6px]' data-tooltip-id='writer-reply-length'>
            <FieldDescription>
              <T keyName='eleo-write-text-length'>Length</T>
            </FieldDescription>
            <WriterLengthContainer
              handleRadioButtonCheck={handleRadioButtonCheck}
              selectedWordLength={wordLength}
              setWriterLengthOption={setWriterLengthOption}
              writerLengthOption={writerLengthOption}
              forceMobile={!isPanelLarge}
            />
          </div>

          {/* Creativity */}
          <div className='space-y-[6px]' data-tooltip-id='writer-reply-creativity'>
            <FieldDescription>
              <T keyName='eleo-write-text-creativity'>Creativity</T>
            </FieldDescription>
            <div className='w-full'>
              <ButtonGroup
                options={Creativity}
                onClick={(event) => setCreativity(event.target.value)}
                value={creativity}
                activeBgClass='bg-[#6363e51a]'
                className='!flex flex-shrink'
              />
            </div>
          </div>

          <div className='col-span-2 mb-4' />
        </div>
      </div>
    )
  )
}
